import { useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Landing } from './pages/public/Landing.js';
import { Login } from './pages/public/Login.js';
import { GymFinder } from './pages/public/GymFinder.js';
import { GymDetail } from './pages/public/GymDetail.js';
import { Checkout } from './pages/public/Checkout.js';
import { MembershipCheckout } from './pages/public/MembershipCheckout.js';
import { BookingConfirm } from './pages/public/BookingConfirm.js';
import { MembershipConfirm } from './pages/public/MembershipConfirm.js';
import { EmailVerification } from './pages/public/EmailVerification.js';
import { MagicLinkVerify } from './pages/public/MagicLinkVerify.js';
import { ResetPassword } from './pages/public/ResetPassword.js';
import { LegalPage } from './pages/public/Legal.js';
import { MyBookings } from './pages/customer/MyBookings.js';
import { Referrals } from './pages/customer/Referrals.js';
import { Settings } from './pages/customer/Settings.js';
import { MyMembership } from './pages/customer/MyMembership.js';
import { AdminDashboard } from './pages/admin/AdminDashboard.js';
import { AdminSectionPage } from './pages/admin/AdminSectionPage.js';
import type { AdminSection } from './pages/admin/AdminSectionPage.js';
import { ProtectedRoute } from './components/layout/ProtectedRoute.js';
import { ProfileLayout } from './components/layout/ProfileLayout.js';
import { AdminChromeProvider } from './components/layout/AdminLayout.js';
import { AtomFitNav } from './components/design/AtomFitUI.js';
import { MarketingUnsubscribe } from './pages/public/MarketingUnsubscribe.js';
import { LanguageSwitcher } from './components/common/LanguageSwitcher.js';

const adminSections: AdminSection[] = [
  'gyms',
  'bookings',
  'users',
  'slots',
  'codes',
  'credits',
  'memberships',
  'promos',
  'referrals',
  'franchise',
  'emails',
  'settings',
];

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { pathname } = location;
  const previousPathnameRef = useRef(pathname);
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
  const isLeavingLanding = previousPathnameRef.current === '/' && pathname !== '/';
  const transitionKey = isAdminRoute
    ? 'admin'
    : ['/reservations', '/membership', '/referrals', '/settings'].includes(pathname)
    ? 'profile'
    : pathname;
  const hasSharedNav =
    pathname === '/' ||
    pathname === '/profile' ||
    pathname === '/reservations' ||
    pathname === '/membership' ||
    pathname === '/referrals' ||
    pathname === '/settings' ||
    pathname === '/gyms' ||
    pathname.startsWith('/gyms/') ||
    pathname === '/checkout' ||
    pathname === '/membership-checkout' ||
    pathname === '/checkout-debug' ||
    pathname === '/booking-confirmed' ||
    pathname === '/membership-confirmed';
  const hasEmbeddedNav = pathname === '/terms' || pathname === '/privacy';
  const showStandaloneLanguageSwitcher = !hasSharedNav && !hasEmbeddedNav && !isAdminRoute;

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;

    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    root.style.scrollBehavior = previousScrollBehavior;
  }, [pathname]);

  useEffect(() => {
    previousPathnameRef.current = pathname;
  }, [pathname]);

  return (
    <>
      {hasSharedNav ? <AtomFitNav /> : null}
      {showStandaloneLanguageSwitcher ? (
        <LanguageSwitcher className="af-language-switcher--floating" />
      ) : null}
      {isLeavingLanding ? <div className="af-landing-exit-veil" aria-hidden="true" /> : null}
      <div
        className={
          isAdminRoute
            ? 'af-page-static'
            : `af-page-transition${pathname === '/' ? ' af-page-transition--landing' : ''}`
        }
        key={transitionKey}
      >
        <Routes location={location}>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/email-preferences/unsubscribe" element={<MarketingUnsubscribe />} />
          <Route path="/gyms" element={<GymFinder />} />
          <Route path="/gyms/:gymId" element={<GymDetail />} />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute requireAccount>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/membership-checkout"
            element={
              <ProtectedRoute requireAccount>
                <MembershipCheckout />
              </ProtectedRoute>
            }
          />
          <Route path="/checkout-debug" element={<Checkout />} />
          <Route path="/booking-confirmed" element={<BookingConfirm />} />
          <Route path="/membership-confirmed" element={<MembershipConfirm />} />
          <Route path="/auth/verify-email" element={<EmailVerification />} />
          <Route path="/auth/verify" element={<MagicLinkVerify />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route
            path="/auth/create-password"
            element={
              <ProtectedRoute requireAccount>
                <ResetPassword initial />
              </ProtectedRoute>
            }
          />

          {/* Customer portal */}
          <Route path="/profile" element={<Navigate to="/reservations" replace />} />
          <Route
            element={
              <ProtectedRoute requireAccount>
                <ProfileLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/reservations" element={<MyBookings />} />
            <Route path="/membership" element={<MyMembership />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          <Route
            element={
              <ProtectedRoute requireRole="OWNER_ADMIN">
                <AdminChromeProvider />
              </ProtectedRoute>
            }
          >
            <Route
              path="/admin"
              element={<AdminDashboard />}
            />
            {adminSections.map((section) => (
              <Route
                key={section}
                path={`/admin/${section}`}
                element={<AdminSectionPage section={section} />}
              />
            ))}
          </Route>
        </Routes>
      </div>
    </>
  );
}
